import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { format } from 'date-fns';
import { BASE_URL } from '../../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faTableList,
  faIdCard,
  faUser,
  faBuilding,
  faClipboard,
  faUserTag,
  faCalendar,
  faEnvelope,
  faExclamationTriangle,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faFilter,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';

const MemberEnrollmentProcessedHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    employee_id: '',
    last_name: '',
    corp_code: '',
    plan_code: '',
    member_type: '',
    activation_date: '',
    expiry_date: '',
    status: '',
    processed_at_from: '',
    processed_at_to: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'processed_at',
    direction: 'desc',
  });
  const statusMap = {
    success: { name: 'Success', color: 'bg-green-100 text-green-800', icon: faCheckCircle },
    failed: { name: 'Failed', color: 'bg-red-100 text-red-800', icon: faTimesCircle },
  };
  const columns = [
    { key: 'employee_id', label: 'Employee ID', sortable: true, icon: faIdCard },
    { key: 'last_name', label: 'Last Name', sortable: true, icon: faUser },
    { key: 'first_name', label: 'First Name', sortable: true, icon: faUser },
    { key: 'corp_code', label: 'Corp Code', sortable: true, icon: faBuilding },
    { key: 'plan_code', label: 'Plan Code', sortable: true, icon: faClipboard },
    { key: 'member_type', label: 'Type', sortable: true, icon: faUserTag },
    { key: 'activation_date', label: 'Activation Date', sortable: true, icon: faCalendar },
    { key: 'expiry_date', label: 'Expiration Date', sortable: true, icon: faCalendar },
    { key: 'email_add', label: 'Email', sortable: true, icon: faEnvelope },
    { key: 'processed_by', label: 'Processed By', sortable: true, icon: faUser },
    { key: 'status', label: 'Status', sortable: true, icon: faCheckCircle },
    { key: 'error_message', label: 'Error Message', sortable: false, icon: faExclamationTriangle },
    { key: 'processed_at', label: 'Processed At', sortable: true, icon: faClock },
  ];

  const isValidDateValue = (value) => {
    if (!value) return false;
    if (typeof value === 'string' && value.startsWith('0000-00-00')) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime());
  };

  const formatSafeDate = (value, dateFormat) => {
    if (!isValidDateValue(value)) return '-';
    try {
      return format(new Date(value), dateFormat);
    } catch (error) {
      return '-';
    }
  };
  useEffect(() => {
    fetchProcessedHistory();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);
  const fetchProcessedHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/members/batch-enrollment/processed`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setTransactions(response.data.data.data);
      setTotalPages(response.data.data.last_page);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this.', { duration: 4000 });
      }
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again later.', { duration: 4000 });
      }
      toast.error('Failed to fetch processed history.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const buildExportFileName = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `member_enrollment_processed_${stamp}.xlsx`;
  };

  const mapTransactionForExport = (transaction) => ({
    'Employee ID': transaction.employee_id || '',
    'Last Name': transaction.last_name || '',
    'First Name': transaction.first_name || '',
    'Corp Code': transaction.corp_code || '',
    'Plan Code': transaction.plan_code || '',
    'Member Type':
      transaction.member_type === 'p'
        ? 'Principal'
        : transaction.member_type === 'd'
        ? 'Dependent'
        : '',
    'Activation Date': formatSafeDate(transaction.activation_date, 'yyyy-MM-dd'),
    'Expiration Date': formatSafeDate(transaction.expiry_date, 'yyyy-MM-dd'),
    Email: transaction.email_add || '',
    'Processed By': transaction.processed_by || transaction.processed_by_username || '',
    Status: statusMap[transaction.status]?.name || transaction.status || '',
    'Error Message': transaction.error_message || '',
    'Processed At': formatSafeDate(transaction.processed_at, 'yyyy-MM-dd HH:mm'),
  });

  const handleExportExcel = async () => {
    if (exporting) return;

    setExporting(true);
    try {
      const perPage = 500;
      let page = 1;
      let lastPage = 1;
      const allRows = [];

      do {
        const response = await axios.get(`${BASE_URL}/members/batch-enrollment/processed`, {
          params: {
            page,
            per_page: perPage,
            search: searchTerm,
            sort_by: sortConfig.key,
            sort_direction: sortConfig.direction,
            ...filters,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        });

        const payload = response?.data?.data || {};
        const pageRows = payload?.data || [];
        lastPage = payload?.last_page || page;
        allRows.push(...pageRows);
        page += 1;
      } while (page <= lastPage);

      if (!allRows.length) {
        toast.error('No processed data to export.', { duration: 4000 });
        return;
      }

      const exportRows = allRows.map(mapTransactionForExport);
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Processed Data');
      XLSX.writeFile(workbook, buildExportFileName());

      toast.success(`Exported ${allRows.length} row(s).`, { duration: 4000 });
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        toast.error('You do not have permission to export this.', { duration: 4000 });
      } else {
        toast.error('Failed to export processed history.', { duration: 4000 });
      }
    } finally {
      setExporting(false);
    }
  };
  const renderPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const range = [];
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }
    if (currentPage - delta > 2) range.unshift('...');
    if (currentPage + delta < totalPages - 1) range.push('...');
    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);
    return pages.map((p, i) => (
      <button
        key={i}
        onClick={() => typeof p === 'number' && handlePageChange(p)}
        disabled={p === '...'}
        className={`p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm ${
          currentPage === p ? 'bg-indigo-500 text-white shadow-md' : ''
        }`}
      >
        {p}
      </button>
    ));
  };
  const LoadingSkeleton = ({ rows, columns }) => (
    <tbody className="bg-white divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="hover:bg-gray-50">
          {Array.from({ length: columns }).map((_, j) => (
            <td key={j} className="px-3 py-2">
              <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6">
      <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-xl overflow-hidden border border-white/20">
          <div className="p-4 sm:p-6 border-b border-gray-200/50">
            <h1 className="text-xl font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faTableList} className="mr-2 text-indigo-600" />
              Batch Member Enrollment: Processed Data
            </h1>
          </div>
          {/* Filters/Search */}
          <div className="p-4 sm:p-6 bg-gray-50/80">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              {/* Example filters - expand as needed */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Corp Code"
                  value={filters.corp_code}
                  onChange={(e) => handleFilterChange('corp_code', e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faFilter}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  </select>
              </div>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faClock}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="date"
                  value={filters.processed_at_from}
                  onChange={(e) => handleFilterChange('processed_at_from', e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  aria-label="Processed At From"
                  title="Processed At From"
                />
              </div>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faClock}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="date"
                  value={filters.processed_at_to}
                  onChange={(e) => handleFilterChange('processed_at_to', e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  aria-label="Processed At To"
                  title="Processed At To"
                />
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                aria-label="Items per page"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    {val} per page
                  </option>
                ))}
              </select>
              <button
                onClick={fetchProcessedHistory}
                className="inline-flex items-center px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-all duration-200 shadow-sm"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExportExcel}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all duration-200 shadow-sm disabled:opacity-60"
                disabled={loading || exporting}
              >
                <FontAwesomeIcon icon={faDownload} className={`mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                {exporting ? 'Exporting...' : 'Export To Excel'}
              </button>
            </div>
          </div>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-all"
                      onClick={column.sortable ? () => handleSort(column.key) : undefined}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={column.icon} className="mr-1 text-xs text-gray-400" />
                        {column.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="p-4 text-center text-gray-500 text-sm">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className={`hover:bg-gray-50 transition-all duration-200 ${transaction.status === 'failed' ? 'bg-red-50/50' : ''}`}
                    >
                      <td className="px-3 py-2 text-sm font-medium">{transaction.employee_id || '-'}</td>
                      <td className="px-3 py-2 text-sm">{transaction.last_name || '-'}</td>
                      <td className="px-3 py-2 text-sm">{transaction.first_name || '-'}</td>
                      <td className="px-3 py-2 text-sm">{transaction.corp_code || '-'}</td>
                      <td className="px-3 py-2 text-sm">{transaction.plan_code || '-'}</td>
                      {/* <td className="px-3 py-2 text-sm">{transaction.member_type || '-'}</td> */}
                      <td className="px-3 py-2 text-sm">
                        {transaction.member_type === 'p'
                          ? 'Principal'
                          : transaction.member_type === 'd'
                          ? 'Dependent'
                          : '-'}
                      </td>

                      <td className="px-3 py-2 text-sm">
                        {formatSafeDate(transaction.activation_date, 'yyyy-MM-dd')}
                    </td>
                      <td className="px-3 py-2 text-sm">
                        {formatSafeDate(transaction.expiry_date, 'yyyy-MM-dd')}
                      </td>
                      <td className="px-3 py-2 text-sm">{transaction.email_add || '-'}</td>
                      <td className="px-3 py-2 text-sm">{transaction.processed_by || transaction.processed_by_username || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium flex items-center ${
                            statusMap[transaction.status]?.color || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={statusMap[transaction.status]?.icon || faCheckCircle}
                            className="mr-1 text-xs"
                          />
                          {statusMap[transaction.status]?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-red-600">
                        {transaction.error_message ? (
                          <span data-tooltip-id="error-tooltip" data-tooltip-content={transaction.error_message}>
                            {transaction.error_message.substring(0, 40)}...
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {formatSafeDate(transaction.processed_at, 'yyyy-MM-dd HH:mm')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center justify-between p-4 sm:p-6 border-t border-gray-100/50 bg-gray-50/50">
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="mb-3 md:mb-0 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
              aria-label="Items per page"
            >
              {[5, 10, 20, 50, 100].map((val) => (
                <option key={val} value={val}>
                  {val} per page
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <div className="flex items-center gap-1 px-2 py-1">
                {renderPageNumbers()}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
        <Tooltip id="refresh-tooltip" place="top" />
        <Tooltip id="error-tooltip" place="top" />
      </div>
    </div>
  );
};
export default MemberEnrollmentProcessedHistory;
